import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  addDoc,
  collection,
  doc,
  getFirestore,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';

import { scoringApp } from '../firebase';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Announcement {
  id: string;
  text: string;
  createdAt: Date;
  createdByName: string;
}

export interface AnnouncementsContextValue {
  announcements: readonly Announcement[];
  undismissed: readonly Announcement[];
  isLoading: boolean;
  dismiss: (id: string) => void;
  create: (text: string, createdByName: string) => Promise<void>;
  clearAll: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

export const AnnouncementsContext = createContext<AnnouncementsContextValue | null>(null);

export function useAnnouncements(): AnnouncementsContextValue {
  const ctx = useContext(AnnouncementsContext);
  if (!ctx) throw new Error('useAnnouncements must be used within AnnouncementsProvider');
  return ctx;
}

// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------

function dismissedKey(tryoutId: string): string {
  return `cheereval_dismissed_${tryoutId}`;
}

function loadDismissed(tryoutId: string): Set<string> {
  try {
    const raw = localStorage.getItem(dismissedKey(tryoutId));
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((x): x is string => typeof x === 'string'));
  } catch {
    return new Set();
  }
}

function saveDismissed(tryoutId: string, dismissed: ReadonlySet<string>): void {
  localStorage.setItem(dismissedKey(tryoutId), JSON.stringify(Array.from(dismissed)));
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface Props {
  tryoutId: string;
  children: React.ReactNode;
}

export function AnnouncementsProvider({ tryoutId, children }: Props) {
  const db = getFirestore(scoringApp);

  const [announcements, setAnnouncements] = useState<readonly Announcement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(() => loadDismissed(tryoutId));
  const isInitialSnapshotRef = useRef(true);

  // useEffect is appropriate here: subscribing to a Firestore real-time listener.
  useEffect(() => {
    const q = query(
      collection(db, 'tryouts', tryoutId, 'announcements'),
      orderBy('createdAt', 'desc'),
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const items: Announcement[] = snap.docs.map((docSnap) => {
          const data = docSnap.data() as Record<string, unknown>;
          const ts = data.createdAt;
          return {
            id: docSnap.id,
            text: typeof data.text === 'string' ? data.text : '',
            createdAt: ts instanceof Timestamp ? ts.toDate() : new Date(),
            createdByName: typeof data.createdByName === 'string' ? data.createdByName : 'Coach',
          };
        });
        setAnnouncements(items);
        setIsLoading(false);

        // On the first snapshot, dismiss all pre-existing announcements so a
        // coach joining mid-tryout isn't bombarded with old toasts.
        if (isInitialSnapshotRef.current) {
          isInitialSnapshotRef.current = false;
          setDismissed((prev) => {
            const next = new Set(prev);
            let changed = false;
            items.forEach((a) => {
              if (!next.has(a.id)) { next.add(a.id); changed = true; }
            });
            if (changed) saveDismissed(tryoutId, next);
            return changed ? next : prev;
          });
        }
      },
      () => {
        setIsLoading(false);
      },
    );

    return unsubscribe;
  }, [db, tryoutId]);

  const dismiss = useCallback((id: string) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(id);
      saveDismissed(tryoutId, next);
      return next;
    });
  }, [tryoutId]);

  const clearAll = useCallback(async () => {
    if (announcements.length === 0) return;
    const batch = writeBatch(db);
    announcements.forEach((a) => {
      batch.delete(doc(collection(db, 'tryouts', tryoutId, 'announcements'), a.id));
    });
    await batch.commit();
    setDismissed(new Set());
    saveDismissed(tryoutId, new Set());
  }, [db, tryoutId, announcements]);

  const create = useCallback(async (text: string, createdByName: string) => {
    const ref = await addDoc(collection(db, 'tryouts', tryoutId, 'announcements'), {
      text,
      createdByName,
      createdAt: Timestamp.now(),
    });
    window.setTimeout(() => {
      setDismissed((prev) => {
        const next = new Set(prev);
        next.add(ref.id);
        saveDismissed(tryoutId, next);
        return next;
      });
    }, 3000);
  }, [db, tryoutId]);

  const undismissed = useMemo(
    () => announcements.filter((a) => !dismissed.has(a.id)),
    [announcements, dismissed],
  );

  const value = useMemo<AnnouncementsContextValue>(
    () => ({ announcements, undismissed, isLoading, dismiss, create, clearAll }),
    [announcements, undismissed, isLoading, dismiss, create, clearAll],
  );

  return (
    <AnnouncementsContext.Provider value={value}>
      {children}
    </AnnouncementsContext.Provider>
  );
}
